import React, { memo, useContext, useMemo, useState } from 'react';
import classnames from 'classnames';
import Countdown from "react-countdown";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { getRandomInt } from '../../apps/Cards/utils/utils';
import StandartIcon from '../../icons/standart.svg';
import PremiumIcon from '../../icons/premium.svg';
import CheckIcon from '../../apps/Cards/icons/check.svg';
import PokerCoachingIcon from '../../apps/Cards/icons/pokerCoaching.svg';
import { LockIcon } from '../../apps/Cards/icons/Lock';
import PlayIcon from "../../apps/Cards/icons/play.svg";
import ClockIcon from '../../apps/Cards/icons/clock.svg';
import { ImageCover } from './ImageCover';
import { PopupCardSeries } from '../PopupCardSeries';
import { PopupAccess } from '../PopupAccess';
import { getMediaUrl } from '../../utils/utils';
import { AppContext } from '../../AppContext';
import './Card.scss';
import AppLink from '../AppLink';

export const Card = memo(function Card({
  parentObjectTitle,
  isShowDate = true,
  isShowWatchLater = true,
  addPopupSeriesSearchParam,
  isRow,
  isSeria,
  type,
  seriaNumber,
  cardItem: {
    title, post_name, cover, cover_srcset, cover_lowres, cover_auto, cover_title,  duration, date, coach, access, freemium, watched, watch_later, has_access, forceRouting, tags, series, progress, freemiumCountdown, hide_from_listing
  },
  cardItem,
  cardView,
  className,
  onToggleWatchLater,
  onClick,
  ...props
}) {
  const { user, coaches } = useContext(AppContext);
  const [isWatchLaterEnabled, setIsWatchLaterEnabled] = useState(true);
  const [isShowPopupCardSeries, setIsShowPopupCardSeries] = useState(false);
  const [isShowPopupAccess, setIsShowPopupAccess] = useState(false);

  const isLegacy = useMemo(() => tags.some(tag => tag.slug.includes('legacy-')), [tags]);

  const coachImage = useMemo(() => {
    if(!cover_auto) {
      return null;
    }

    const currentCoaches = coach.map((coachId) => coaches.get(coachId));
    const coachWithImages = currentCoaches.find((coach) => coach.thumbnail_image_pack);

    if(!coachWithImages) {
      return null;
    }

    const coachImages = coachWithImages.thumbnail_image_pack;
    const randomImageIndex = getRandomInt(0, coachImages.length - 1);

    return coachImages[randomImageIndex];
  }, [cover_auto, coach, coaches]);

  const hasSeries = series?.length > 0;

  function showCardSeries() {
    setIsShowPopupCardSeries(true);
  }

  function handleCloseCardSeries() {
    setIsShowPopupCardSeries(false);
  }

  function handleClick() {
    onClick && onClick(cardItem);

    if(hasSeries) {
      showCardSeries();
      return;
    }

    if(!has_access && !forceRouting) {
      setIsShowPopupAccess(true);
    }
  }

  function handleClosePopupAccess(e) {
    e.stopPropagation();
    setIsShowPopupAccess(false);
  }

  async function handleWatchLaterClick() {
    //actid=29
    window.analytics?.track("watch_later:click", {
      "action": "class_watch_later_flag",
      "value": !watch_later,
      "parent_object_title": parentObjectTitle,
      "title": title,
      "url": window.location.href
    });

    setIsWatchLaterEnabled(false);
    await onToggleWatchLater(cardItem);
    setIsWatchLaterEnabled(true);
  }

  if (hide_from_listing) return null;

  if(hasSeries) {
    return (
      <div className="Card Card_fullHeight" onClick={handleClick} {...props}>
        <div className="Card__image-block">
          <LazyLoadImage
            className="Card__image"
            srcSet={!coachImage && cover_srcset ? cover_srcset : undefined}
            sizes={!coachImage && cover_srcset ? '(max-width: 480px) 300px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1280px' : undefined}
            src={coachImage || cover || cover_lowres?.[0] || undefined}
            alt=""
          />

          <div className="Card__series-badge">
            <img src={getMediaUrl(PokerCoachingIcon)} alt="" />
            <span>premium series</span>
          </div>

          {duration && (
            <span className="Card__duration">{duration}</span>
          )}
        </div>

        {isShowPopupCardSeries && (
          <PopupCardSeries
            searchParam={addPopupSeriesSearchParam}
            card={cardItem}
            closeModal={handleCloseCardSeries}
          />
        )}
      </div>
    );
  }

  const content = (
    <div className={classnames('Card', {
      'Card_row': isRow,
      'Card_seria': isSeria,
      'Card_fade': watched
    }, className)} onClick={handleClick} {...props}>
      {isSeria && seriaNumber && (
        <span className="Card__number">{seriaNumber}</span>
      )}

      <div className="Card__image-wrapper">
        <div className="Card__image-block">
          <LazyLoadImage
            className="Card__image"
            srcSet={!coachImage && cover_srcset ? cover_srcset : undefined}
            sizes={!coachImage && cover_srcset ? '(max-width: 480px) 300px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1280px' : undefined}
            src={coachImage || cover || cover_lowres?.[0] || undefined}
            alt=""
          />

          {cover_auto && coachImage && (
            <ImageCover color="#48A534" text={cover_title} />
          )}

          {watched && (
            <img className="Card__passed-icon" src={getMediaUrl(CheckIcon)} alt="" />
          )}

          {((!has_access && !freemiumCountdown && user.type !== 'guest') || (!has_access && user.type === 'guest')) && (
            <LockIcon className="Card__locked-icon" />
          )}

          {has_access && isShowWatchLater && !isSeria && user.type !== 'guest' && (
            <div
              className={classnames('Card__watch-later', { 'Card__watch-later_disabled': !isWatchLaterEnabled || !has_access })}
              onClick={isWatchLaterEnabled && has_access ? handleWatchLaterClick : undefined}
            >
              <div className="Card__watch-later-icon">
                {watch_later
                  ? <img src={getMediaUrl(CheckIcon)} alt="" />
                  : <img src={getMediaUrl(ClockIcon)} alt="" />
                }
              </div>
              <div className="Card__watch-later-label">
                <span className="Card__watch-later-label-text">
                  {watch_later
                    ? 'Added to Watch later'
                    : 'Watch later'
                  }
                </span>
              </div>
            </div>
          )}

          <div className="Card__play">
            <img src={getMediaUrl(PlayIcon)} alt="" />
          </div>

          {!isSeria && access === "Free" && !isLegacy && !freemiumCountdown && (
            <span className="Card__access">{access}</span>
          )}

          {!isSeria && freemiumCountdown && (
            <Countdown className="Card__access Card__countdown" date={new Date(freemium.freemium_period_end)} />
          )}

          {!isRow && !isSeria && duration && (
            <span className="Card__duration">{duration}</span>
          )}

          {progress && (
            <div className="Card__progress">
              <div className="Card__progress-val" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="Card__subs-icons">
            {access === 'Standard' && (
              <>
                <img className="Card__subs-icon" src={getMediaUrl(StandartIcon)} alt="" />
                <img className="Card__subs-icon" src={getMediaUrl(PremiumIcon)} alt="" />
              </>
            )}

            {access === 'Premium' && (
              <img className="Card__subs-icon" src={getMediaUrl(PremiumIcon)} alt="" />
            )}
          </div>
        </div>
      </div>

      <div className="Card__info">
        <div className="Card__info-row Card__info-row_no-wrap">
          <h3 className="Card__title">
            <span dangerouslySetInnerHTML={{ __html: title }} />
          </h3>

          <div className="Card__box">
            {isSeria && duration && (
              <div className="Card__duration">{duration}</div>
            )}
            {isSeria && post_name && (
              <div className="Card__date">{date}</div>
            )}
          </div>
          
        </div>

        {!isRow && !isSeria && isShowDate && (
          <div className="Card__date">{date}</div>
        )}

        <div className="Card__info-row">
          {isSeria && (
            <div className="Card__info-row-break" />
          )}

          {!isSeria && isRow && (
            <ul className="Card__meta">
              {duration && (
                <li className="Card__meta-item">Duration: {duration}</li>
              )}
              <li className="Card__meta-item">Date Added: {date}</li>
              <li className="Card__meta-item">
                {tags.map((tag) => tag.name).join(', ')}
              </li>
            </ul>
          )}
        </div>
      </div>

      {isShowPopupAccess && (
        <PopupAccess access={access} closeModal={handleClosePopupAccess} redirectionObjectTitle={title}/>
      )}
    </div>
  );

  function handleLinkClick(e) {
    if(e.target.closest('.Card__watch-later')) {
      e.preventDefault();
    }
  }

  if(post_name && (has_access || forceRouting)) {

    const contentType = type === 'challenges' ? 'courses' : type;
    
    return (
      <AppLink to={`/${contentType}/${post_name}/`} className="Card_link" onClick={handleLinkClick}>
        {content}
      </AppLink>
    );
  }

  return content;
});
